import React from 'react';
import { Link } from 'react-router-dom';
import { FaCog, FaMicrochip, FaCloud, FaBroadcastTower, FaNetworkWired, FaTools, FaBox, FaShieldAlt } from 'react-icons/fa';
import './HomePage.css';

function HomePage() {
  const services = [
    {
      icon: <FaCog />,
      title: 'PLC & HMI Programming',
      description: 'Expert PLC & HMI programming in multiple languages (Structured Text, Ladder, Function Block Diagram, Sequential Function Chart) for industrial automation and process control. Multi Vendor support.',
      details: ['Siemens S7 PLCs', 'Allen-Bradley PLCs', 'Mitsubishi PLCs', 'Drives (Siemens/Allen-Bradley/Parker/Stober)', 'LabVIEW', 'Wonderware SCADA', 'WinCC HMI & SCADA V5-V8', 'GOT HMI']
    },
    {
      icon: <FaMicrochip />,
      title: 'Microchip Development',
      description: 'Advanced microcontroller development and embedded systems design for specialised industrial applications.',
      details: ['ARM Cortex', 'Espressif', 'PCB Design & Assembly', 'Real-time Systems', 'Custom Firmware', 'Power Optimization']
    },
    {
      icon: <FaCloud />,
      title: 'Cloud Computing',
      description: 'Cloud infrastructure solutions for data analytics, remote monitoring, and scalable industrial applications.',
      details: ['AWS Integration', 'Real-time Analytics', 'Secure Data Handling', 'Scalable Architecture']
    },
    {
      icon: <FaBroadcastTower />,
      title: 'IoT Devices',
      description: 'IoT device development and integration for smart factory automation and predictive maintenance.',
      details: ['Sensor Networks', 'MQTT Protocols', 'Edge Computing', 'Device Management']
    },
    {
      icon: <FaNetworkWired />,
      title: 'Networks',
      description: 'Advanced networking solutions for industrial communication and device connectivity across diverse protocols and standards. Multi-protocol expertise for seamless integration of industrial devices and systems.',
      details: ['WiFi & Bluetooth LE', 'Zigbee', 'RS485/RS232', 'Modbus ASCII/RTU/TCP-IP', 'MTConnect', 'CANBus', 'Ethernet/IP', 'PROFIBUS/PROFINET', 'OPC UA/DA','CC-Link', 'Protocol Gateways', 'Network Security', 'Industrial Firewalls', 'VPN Solutions', 'Kepware']
    },
    {
      icon: <FaBox />,
      title: 'Industrial Panels & Hardware',
      description: 'Complete electrical panel design, build, and installation services. From custom control enclosure design and schematic creation to on-site installation and refurbishment of existing systems.',
      details: ['Control Panel Design', 'Electrical Schematics', 'Custom Enclosure Build', 'Panel Assembly & Wiring', 'On-Site Installation', 'System Refurbishment', 'Safety Compliance Testing']
    },
    {
      icon: <FaTools />,
      title: 'Support & Breakdown Services',
      description: 'Rapid breakdown support and maintenance for industrial machines. 24/7 on-call availability, with multiskilled capabilities for comprehensive repairs.',
      details: ['24/7 On-Call Support', 'Emergency Breakdown Response', 'Multiskilled Repairs', 'Preventive Maintenance', 'Machine Diagnostics', 'Field Service']
    },
    {
      icon: <FaTools />,
      title: 'Machinery Safety & Compliance',
      description: 'Comprehensive machinery safety assessments and functional safety solutions for industrial environments.',
      subSections: [
        {
          title: 'PUWER Assessments',
          items: ['Machine Functional Safety', 'Process Safety', 'SIL Assessment']
        },
        {
          title: 'Lifecycle Management',
          items: ['Policies & Procedures', 'Documentation & Records', 'Compliance Monitoring']
        }
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cyber Security & Asset Protection',
      description: 'Industrial cybersecurity frameworks, risk assessments, and protection for connected operations.',
      subSections: [
        {
          title: 'Security Assessment & Framework',
          items: ['CRA Assessment', 'Security Level Determination', 'Framework Implementation']
        },
        {
          title: 'Asset & Network Protection',
          items: ['Asset Registers', 'Network Architecture Reviews', 'Access Control Validation']
        }
      ]
    }

  ];

  return (
    <div
      className="home-lab-shell"
      style={{
        '--lab-bg': '#060913',
        '--lab-panel': '#0d1320',
        '--lab-line': 'rgba(0, 212, 255, 0.15)',
        '--lab-cyan': '#00d4ff',
        '--lab-text': '#f4f8ff',
        '--lab-muted': '#9baabd',
        background: 'var(--lab-bg)',
        minHeight: '100vh'
      }}
    >
      <style>{`
        .home-lab-shell {
          overflow: hidden;
        }

        .home-lab-hero {
          position: relative;
          isolation: isolate;
          padding: clamp(110px, 15vw, 190px) var(--lab-gutter, 24px) clamp(90px, 12vw, 150px);
          color: var(--lab-text);
          background: radial-gradient(circle at 72% 12%, #0f1424 0%, #060913 68%);
          border-bottom: 1px solid var(--lab-line);
          text-align: center;
        }

        .home-lab-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          opacity: 0.42;
          background-image: linear-gradient(rgba(0, 212, 255, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.08) 1px, transparent 1px);
          background-size: 64px 64px;
          mask-image: linear-gradient(to bottom, black, transparent 86%);
        }

        .home-lab-hero-content,
        .home-lab-tracks-inner {
          width: min(1180px, 100%);
          margin: 0 auto;
        }

        .home-lab-kicker,
        .home-lab-track-label {
          color: var(--lab-cyan);
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }

        .home-lab-kicker {
          margin-bottom: 24px;
        }

        .home-lab-hero h1 {
          max-width: 900px;
          margin: 0 auto;
          font-size: clamp(3rem, 8vw, 7.2rem);
          font-weight: 800;
          letter-spacing: -0.06em;
          line-height: 0.94;
          text-wrap: balance;
        }

        .home-lab-hero .home-lab-hero-summary {
          max-width: 680px;
          margin: 32px auto 0;
          color: var(--lab-muted);
          font-size: clamp(1rem, 1.7vw, 1.2rem);
          line-height: 1.75;
        }

        .home-lab-hero .home-lab-kicker {
          margin-bottom: 24px;
          color: var(--lab-cyan);
        }

        .home-lab-hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-top: 36px;
          justify-content: center;
        }

        .home-lab-hero-actions .btn {
          min-height: 52px;
          display: inline-flex;
          align-items: center;
          border-radius: 3px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .home-lab-hero-actions .btn-primary {
          border: 1px solid var(--lab-cyan);
          background: var(--lab-cyan);
          color: #041018;
          box-shadow: 0 14px 30px rgba(0, 212, 255, 0.16);
        }

        .home-lab-hero-actions .btn-secondary {
          border: 1px solid var(--lab-line);
          background: rgba(255, 255, 255, 0.04);
          color: var(--lab-text);
        }

        .home-lab-hero-actions .btn:hover {
          transform: translateY(-3px);
        }

        .home-lab-tracks {
          padding: clamp(72px, 10vw, 120px) var(--lab-gutter, 24px);
          color: var(--lab-text);
          background: var(--lab-bg);
          border-bottom: 1px solid var(--lab-line);
        }

        .home-lab-tracks-heading {
          max-width: 680px;
          margin-bottom: 38px;
        }

        .home-lab-tracks-heading h2 {
          margin: 12px 0 0;
          color: var(--lab-text);
          font-size: clamp(2rem, 4vw, 3.8rem);
          font-weight: 800;
          letter-spacing: -0.045em;
          line-height: 1;
        }

        .home-lab-track-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
        }

        .home-lab-track {
          min-width: 0;
          padding: clamp(26px, 4vw, 46px);
          border: 1px solid var(--lab-line);
          background: linear-gradient(145deg, rgba(16, 27, 43, 0.92), rgba(7, 12, 22, 0.96));
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 24px 60px rgba(0, 0, 0, 0.2);
        }

        .home-lab-track h3 {
          margin: 20px 0 16px;
          color: var(--lab-text);
          font-size: clamp(1.5rem, 3vw, 2.35rem);
          font-weight: 800;
          letter-spacing: -0.035em;
          line-height: 1.05;
        }

        .home-lab-track p {
          margin: 0;
          color: var(--lab-muted);
          font-size: 1rem;
          line-height: 1.75;
        }

        .home-lab-compliance {
          padding: clamp(72px, 10vw, 120px) var(--lab-gutter, 24px);
          color: var(--lab-text);
          background: #0b0f19;
          border-bottom: 1px solid var(--lab-line);
        }

        .home-lab-compliance-inner {
          width: min(1180px, 100%);
          margin: 0 auto;
        }

        .home-lab-compliance-header {
          max-width: 700px;
          margin-bottom: 38px;
        }

        .home-lab-compliance-header h2 {
          margin: 12px 0 0;
          color: var(--lab-text);
          font-size: clamp(2rem, 4vw, 3.8rem);
          font-weight: 800;
          letter-spacing: -0.045em;
          line-height: 1;
        }

        .home-lab-compliance-header p:last-child {
          max-width: 620px;
          margin: 20px 0 0;
          color: var(--lab-muted);
          font-size: 1rem;
          line-height: 1.7;
        }

        .home-lab-compliance-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }

        .home-lab-compliance-card {
          min-width: 0;
          padding: clamp(24px, 3vw, 34px);
          border: 1px solid #334155;
          background: linear-gradient(145deg, #1e293b, #121a29);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 18px 44px rgba(0, 0, 0, 0.18);
          transition: border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease;
        }

        .home-lab-compliance-card:hover {
          border-color: rgba(0, 212, 255, 0.5);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 16px 40px rgba(0, 212, 255, 0.08);
          transform: translateY(-3px);
        }

        .home-lab-compliance-index {
          display: block;
          margin-bottom: 28px;
          color: var(--lab-cyan);
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.16em;
        }

        .home-lab-compliance-card h3 {
          margin: 0 0 16px;
          color: var(--lab-text);
          font-size: clamp(1.3rem, 2vw, 1.75rem);
          font-weight: 800;
          letter-spacing: -0.025em;
          line-height: 1.12;
        }

        .home-lab-compliance-card p {
          margin: 0;
          color: #b2bdcc;
          font-size: 0.96rem;
          line-height: 1.75;
        }

        .home-lab-about .container {
          width: min(1180px, 100%);
          margin: 0 auto;
        }

        .home-lab-about h2 {
          color: var(--lab-text);
          font-weight: 800;
        }

        .home-lab-about-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
          gap: 24px;
          align-items: stretch;
        }

        .home-lab-about-statement,
        .home-lab-about-capabilities {
          min-width: 0;
          padding: clamp(28px, 4vw, 44px);
          border: 1px solid var(--lab-line);
          background: linear-gradient(145deg, #0d1320, #090f1a);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 18px 44px rgba(0, 0, 0, 0.2);
        }

        .home-lab-about-label {
          display: block;
          margin-bottom: 26px;
          color: var(--lab-cyan);
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.14em;
          line-height: 1.4;
          text-transform: uppercase;
        }

        .home-lab-about-statement p {
          margin: 0;
          color: #ffffff;
          font-size: clamp(1.3rem, 2.5vw, 2rem);
          font-weight: 800;
          letter-spacing: -0.025em;
          line-height: 1.2;
        }

        .home-lab-about-capabilities ul {
          display: flex;
          flex-direction: column;
          gap: 14px;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .home-lab-about-capabilities li {
          position: relative;
          padding: 0 0 14px 22px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          color: #cbd5e1;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.5;
        }

        .home-lab-about-capabilities li:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .home-lab-about-capabilities li::before {
          content: '\\25AA';
          position: absolute;
          left: 0;
          color: var(--lab-cyan);
          font-size: 15px;
        }

        .cta-premium {
          background: #0d1320 !important;
          border-top: 1px solid rgba(0, 212, 255, 0.12) !important;
          border-bottom: 1px solid rgba(0, 212, 255, 0.06) !important;
          position: relative !important;
        }

        .cta-premium::before,
        .cta-premium::after {
          display: none !important;
          background: none !important;
        }

        .cta-premium-eyebrow {
          color: #00d4ff !important;
          font-weight: 800 !important;
          letter-spacing: 2px !important;
          opacity: 1 !important;
          text-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
        }

        .cta-premium-content {
          background: #060913 !important;
          border: 1px solid #1e293b !important;
          border-left: 4px solid #00d4ff !important;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4) !important;
          border-radius: 4px !important;
        }

        .cta-premium-description {
          color: #94a3b8 !important;
        }

        .cta-premium-action {
          background-color: #38bdf8 !important;
          color: #0f172a !important;
          font-weight: 700 !important;
          border: none !important;
          transition: all 0.2s ease-in-out !important;
        }

        .cta-premium-action:hover {
          background-color: #00d4ff !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 0 20px rgba(0, 212, 255, 0.3) !important;
        }

        @media (max-width: 720px) {
          .home-lab-shell {
            --lab-gutter: 20px;
          }

          .home-lab-track-grid {
            grid-template-columns: 1fr;
          }

          .home-lab-compliance-grid {
            grid-template-columns: 1fr;
          }

          .home-lab-about-grid {
            grid-template-columns: 1fr;
          }

          .home-lab-hero-actions .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
      <main>
      {/* Hero Section */}
      <section className="hero home-lab-hero">
        <div className="home-lab-hero-content">
          <p className="home-lab-kicker">Mission-Critical Engineering / R&amp;D Division</p>
          <h1>Industrial Automation &amp; Embedded R&amp;D</h1>
          <p className="home-lab-hero-summary">Precision engineering for connected machines, resilient controls, and embedded platforms built for demanding industrial environments.</p>
          <div className="hero-buttons home-lab-hero-actions">
            <Link to="/contact" className="btn btn-primary">Get Started</Link>
            <a href="#services" className="btn btn-secondary">Learn More</a>
          </div>
        </div>
      </section>

      <section id="tracks" className="home-lab-tracks" aria-labelledby="strategic-tracks-heading">
        <div className="home-lab-tracks-inner">
          <div className="home-lab-tracks-heading">
            <p className="home-lab-kicker">Three strategic tracks</p>
            <h2 id="strategic-tracks-heading">From first principles to factory floor.</h2>
          </div>
          <div className="home-lab-track-grid">
            <article className="home-lab-track">
              <p className="home-lab-track-label">Track 01 / R&amp;D</p>
              <h3>Embedded Systems R&amp;D &amp; Custom Firmware</h3>
              <p>We architect the intelligent edge: custom firmware, real-time control, microcontroller platforms, and connected embedded products engineered around your exact operating constraints.</p>
            </article>
            <article className="home-lab-track">
              <p className="home-lab-track-label">Track 02 / Industrial Systems</p>
              <h3>19+ Years of Tier-1 Industrial Automation &amp; PLC Systems</h3>
              <p>We bring proven industrial depth to complex production environments, from PLC and HMI systems to machine integration, diagnostics, safety, and long-term operational support.</p>
            </article>
            <article className="home-lab-track">
              <p className="home-lab-track-label">Track 03 / Cloud Industrial Systems</p>
              <h3>Enterprise Cloud IoT &amp; Industrial Analytics Pipelines</h3>
              <p>We bridge physical assets and cloud architecture: designing secure serverless telemetry drops, automated data pipelines, real-time database historians, and executive metrics dashboards tailored for engineering infrastructure.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Services Section - Two Column Layout */}
      <section id="services" className="services-two-column">
        <div className="container">
          <h2>Our Services</h2>
          <p className="section-subtitle">Comprehensive industrial automation solutions tailored to your needs</p>
          
          <div className="services-column-container">
            {services.map((service, index) => (
              <div key={index} className={`service-row ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="service-image-placeholder">
                  <div className="service-icon-large">{service.icon}</div>
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  {service.subSections ? (
                    <div className="service-subsections">
                      {service.subSections.map((subSection, idx) => (
                        <div key={idx} className="service-subsection">
                          <h4>{subSection.title}</h4>
                          <ul className="service-details">
                            {subSection.items.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="service-details">
                      {service.details.map((detail, i) => (
                        <li key={i}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about home-lab-about">
        <div className="container">
          <h2>About NAK Engineering LTD</h2>
          <div className="home-lab-about-grid">
            <div className="home-lab-about-statement">
              <span className="home-lab-about-label">Engineering partner / critical infrastructure</span>
              <p>NAK ENGINEERING LTD is an elite engineering partner providing specialized industrial automation, custom embedded R&amp;D, and secure cloud pipelines for critical machinery infrastructure.</p>
            </div>
            <div className="home-lab-about-capabilities">
              <span className="home-lab-about-label">Procurement capability matrix</span>
              <ul>
                <li>19+ Years of Authoritative Field Experience</li>
                <li>Full-Lifecycle Mechatronics &amp; System Integration</li>
                <li>Multi-Vendor PLC Control Logic (Siemens, Allen-Bradley, Mitsubishi)</li>
                <li>Bare-Metal Firmware Engineering &amp; Metrology Front-Ends</li>
                <li>Serverless Industrial Telemetry Pipelines (AWS S3 &amp; QuickSight)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="home-lab-compliance" style={{ paddingTop: '160px', marginTop: '0px', overflow: 'visible' }} aria-labelledby="compliance-frameworks-heading">
        <div className="home-lab-compliance-inner">
          <div className="home-lab-compliance-header">
            <p className="home-lab-kicker">Enterprise readiness / quality assurance</p>
            <h2 id="compliance-frameworks-heading">Compliance &amp; Quality Frameworks</h2>
            <p>Structured engineering governance for safer machinery, certifiable products, and dependable production outcomes.</p>
          </div>
          <div className="home-lab-compliance-grid">
            <article className="home-lab-compliance-card">
              <span className="home-lab-compliance-index">01 / MACHINE SAFETY</span>
              <h3>PUWER Statutory Assessments</h3>
              <p>Complete machine functional safety, regulatory compliance mapping, and SIL diagnostic validations.</p>
            </article>
            <article className="home-lab-compliance-card">
              <span className="home-lab-compliance-index">02 / CONFORMITY</span>
              <h3>CE / UKCA Conformity Engineering</h3>
              <p>Technical file compilation, rigorous machinery directive compliance engineering, and localized certification testing data panels.</p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta cta-premium">
        <div className="container cta-premium-container">
          <div className="cta-premium-grid" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="cta-premium-content">
            <p className="cta-premium-eyebrow" style={{ color: '#00d4ff', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase', opacity: 1, textShadow: '0 0 15px rgba(0, 212, 255, 0.4)', display: 'block', marginBottom: '16px' }}>
              Engineering the next operating advantage
            </p>
            <h2>Ready to Transform Your Operations?</h2>
            <p className="cta-premium-description">Contact NAK ENGINEERING LTD to discuss how we can help automate your industrial processes with precise, resilient, and intelligent systems.</p>
            <Link to="/contact" className="btn btn-primary cta-premium-action">Contact Us Today <span aria-hidden="true">-&gt;</span></Link>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}

export default HomePage;
